import React, { useState } from "react";

import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";

import {
    CSOSN_OPTIONS,
    CST_OPTIONS,
    IBSCBS_CST_OPTIONS,
    IPI_CST_IN_OPTIONS,
    IPI_CST_OUT_OPTIONS,
    ISSQN_ENFORCEABILITY_OPTIONS,
    PIS_COFINS_CST_IN_OPTIONS,
    PIS_COFINS_CST_OPTIONS,
    csosnNeedsCreditRate,
    desonerationReasonsFor,
    icmsHasFcp,
    icmsHasRate,
    needsDeferral,
    needsDesoneration,
    needsOwnRate,
    needsSubstitution,
} from "../helpers";

// Aceita vírgula ("18,5") e mantém vazio quando o usuário apaga — zero e
// vazio não significam a mesma coisa numa alíquota.
const decimal = value => (value ?? '').toString().replace(/[^\d,.]/g, '');
const onlyDigits = value => (value ?? '').toString().replace(/\D/g, '');

/**
 * O que cada campo mostra, e se a linha diverge da natureza.
 *
 * A tela do item recebe, além do ajuste da linha (`rule`), o que a natureza
 * resolveu para aquele item (`inherited`): o campo exibe o efetivo — o ajuste
 * quando existe, senão o da natureza. A mescla é só de EXIBIÇÃO: continua
 * indo para a nota apenas o que for digitado, senão a linha congelaria uma
 * cópia da natureza e pararia de acompanhar exceção, UF de destino e regime
 * do emitente. Sem `inherited` (as telas da natureza, onde todo valor é da
 * própria regra) a mescla é a regra e nada é marcado como ajuste.
 */
const merge = (rule, inherited, editing, setEditing) => {
    const inherits = inherited !== null;
    const read = (source, path) => {
        const value = path.startsWith('attributes.')
            ? source.attributes?.[path.slice('attributes.'.length)]
            : source[path];

        return value ?? '';
    };

    const own = path => read(rule, path);
    const base = path => read(inherited ?? {}, path);
    const adjusted = path => inherits && own(path) !== '';

    return {
        own,
        base,
        /**
         * Depois que o campo é digitado ele mostra SÓ o que a linha tem —
         * inclusive vazio. Sem isso, apagar o último dígito trazia o valor da
         * natureza de volta e o dígito seguinte entrava emendado nele.
         */
        value: path => {
            if (editing === path) return own(path);

            return own(path) !== '' ? own(path) : base(path);
        },
        /**
         * Selecionar no foco é o que faz a primeira tecla SUBSTITUIR o valor
         * herdado em vez de emendar nele — é como o app já trata campo com
         * máscara. Sair do campo devolve a exibição do efetivo.
         */
        focus: path => ({
            onFocus: event => event.target.select(),
            onBlur: () => setEditing(current => (current === path ? null : current)),
        }),
        cls: (column, path) => (adjusted(path) ? `${column} tax-field adjusted` : column),
        mark: path => {
            if (!adjusted(path)) return null;

            const inheritedValue = base(path);
            const diverges = inheritedValue !== '' && String(inheritedValue) !== String(own(path));

            return (
                <p className="input-hint tax-field-note">
                    {diverges ? `Ajustado — a natureza define ${inheritedValue}` : 'Ajustado nesta linha'}
                </p>
            );
        },
    };
};

const cstOptions = (tax, direction, classifications) => {
        if (tax === 'simples') return CSOSN_OPTIONS;
        if (tax === 'icms') return CST_OPTIONS;
        if (tax === 'ipi') return direction === 'in' ? IPI_CST_IN_OPTIONS : IPI_CST_OUT_OPTIONS;
        // Sete dos 18 CSTs de IBS/CBS não têm nenhuma classificação aceita em
        // NF-e (são de serviço, imóvel ou financeiro). Em vez de manter a
        // exclusão à mão, sai da lista que o backend já devolve recortada por
        // documento — quando a tabela mudar, o select acompanha. Lista vazia
        // é carregamento, não ausência: aí vale a tabela inteira.
        if (tax === 'ibscbs') {
            return classifications.length
                ? IBSCBS_CST_OPTIONS.filter(
                    option => classifications.some(item => item.cst === option.value)
                )
                : IBSCBS_CST_OPTIONS;
        }
        if (tax === 'pis' || tax === 'cofins') {
            return direction === 'in' ? PIS_COFINS_CST_IN_OPTIONS : PIS_COFINS_CST_OPTIONS;
        }

        return null; // ISSQN e DIFAL não têm situação tributária própria na NF-e
    };

const substitutionFields = (view, onChange) => {
        return (
            <div className="section">
                <h4>Substituição tributária</h4>
                <div className="row">
                    <div className={view.cls('col-3', 'attributes.st_rate')}>
                        <Input
                            label={`Alíquota ICMS-ST (%)`}
                            value={view.value('attributes.st_rate')}
                            {...view.focus('attributes.st_rate')}
                            change={value => onChange('attribute', 'st_rate', decimal(value))}
                        />
                        <p className="input-hint">No estado de destino</p>
                        {view.mark('attributes.st_rate')}
                    </div>
                    <div className={view.cls('col-3', 'attributes.st_base')}>
                        <Input
                            label={`Base do ICMS-ST (%)`}
                            value={view.value('attributes.st_base')}
                            {...view.focus('attributes.st_base')}
                            change={value => onChange('attribute', 'st_base', decimal(value))}
                        />
                        {view.mark('attributes.st_base')}
                    </div>
                    <div className={view.cls('col-3', 'attributes.st_mva')}>
                        <Input
                            label={`MVA / IVA (%)`}
                            value={view.value('attributes.st_mva')}
                            {...view.focus('attributes.st_mva')}
                            change={value => onChange('attribute', 'st_mva', decimal(value))}
                        />
                        {view.mark('attributes.st_mva')}
                    </div>
                    <div className={view.cls('col-3', 'attributes.st_fcp_rate')}>
                        <Input
                            label={`FCP do ICMS-ST (%)`}
                            value={view.value('attributes.st_fcp_rate')}
                            {...view.focus('attributes.st_fcp_rate')}
                            change={value => onChange('attribute', 'st_fcp_rate', decimal(value))}
                        />
                        <p className="input-hint">Fundo de combate à pobreza</p>
                        {view.mark('attributes.st_fcp_rate')}
                    </div>
                </div>
            </div>
        );
    };

    /**
     * @param onChange (kind, key, value) — kind 'field' ou 'attribute'
     */
const taxFields = (tax, rule, inherited, change, direction, classifications, editing, setEditing) => {
        const options = cstOptions(tax, direction, classifications);
        const isIcms = tax === 'simples' || tax === 'icms';
        const view = merge(rule, inherited, editing, setEditing);

        // Digitar marca o campo como sob edição. Fica aqui, e não em cada
        // campo, porque a chave que chega já identifica qual é.
        const onChange = (kind, key, value) => {
            setEditing(kind === 'attribute' ? `attributes.${key}` : key);
            change(kind, key, value);
        };
        // Os campos que aparecem dependem do CST EFETIVO: enquanto a condição
        // lia só o ajuste da linha, ST e crédito do Simples ficavam escondidos
        // justamente quando a situação vinha da natureza.
        const cst = view.value('cst');

        return (
            <>
                <div className="row">
                    {options &&
                        <div className={view.cls('col-6', 'cst')}>
                            <InputSelect
                                label={tax === 'simples' ? `Situação tributária (CSOSN)` : `Situação tributária (CST)`}
                                value={cst}
                                change={value => onChange('field', 'cst', value)}
                                // Campo de texto volta a herdar quando é apagado; um
                                // select, não — daí a opção de voltar na lista.
                                options={view.base('cst') !== ''
                                    ? [{ label: `Usar o que a natureza define (${view.base('cst')})`, value: '' }, ...options]
                                    : options
                                }
                            />
                            {view.mark('cst')}
                        </div>
                    }

                    {/* IBS/CBS não tem alíquota aqui de propósito: quem calcula
                        é a Calculadora da Receita Federal, via provedor. */}
                    {tax !== 'ibscbs' && (!isIcms || icmsHasRate(cst) || (tax === 'simples' && cst === '900')) &&
                        <div className={view.cls('col-3', 'rate')}>
                            <Input
                                label={tax === 'difal'
                                    ? `Alíquota interna do destino (%)`
                                    : (needsOwnRate(tax, cst) ? `Alíquota do ICMS próprio (%) *` : `Alíquota (%)`)
                                }
                                value={view.value('rate')}
                                {...view.focus('rate')}
                                change={value => onChange('field', 'rate', decimal(value))}
                            />
                            {view.mark('rate')}
                        </div>
                    }

                    {tax !== 'difal' &&
                        <div className={view.cls('col-3', 'base')}>
                            <Input
                                label={`Base de cálculo (%)`}
                                value={view.value('base')}
                                {...view.focus('base')}
                                change={value => onChange('field', 'base', decimal(value))}
                            />
                            {view.mark('base')}
                        </div>
                    }
                </div>

                {tax === 'simples' && csosnNeedsCreditRate(cst) &&
                    <div className="row">
                        <div className={view.cls('col-4', 'attributes.credit_rate')}>
                            <Input
                                label={`Alíquota de crédito do Simples (%) *`}
                                value={view.value('attributes.credit_rate')}
                                {...view.focus('attributes.credit_rate')}
                                change={value => onChange('attribute', 'credit_rate', decimal(value))}
                            />
                            <p className="input-hint">Obrigatória para o CSOSN {cst}</p>
                            {view.mark('attributes.credit_rate')}
                        </div>
                    </div>
                }

                {tax === 'icms' && needsDeferral(cst) &&
                    <div className="row">
                        <div className={view.cls('col-4', 'attributes.deferral_rate')}>
                            <Input
                                label={`Percentual de diferimento (%)`}
                                value={view.value('attributes.deferral_rate')}
                                {...view.focus('attributes.deferral_rate')}
                                change={value => onChange('attribute', 'deferral_rate', decimal(value))}
                            />
                            {view.mark('attributes.deferral_rate')}
                        </div>
                    </div>
                }

                {isIcms && needsSubstitution(tax, cst) && substitutionFields(view, onChange)}

                {tax === 'icms' && needsDesoneration(cst) &&
                    <div className="section">
                        <h4>Desoneração do ICMS</h4>
                        <div className="row">
                            <div className={view.cls('col-6', 'attributes.desoneration_reason')}>
                                <InputSelect
                                    label={`Motivo`}
                                    value={view.value('attributes.desoneration_reason')}
                                    change={value => onChange('attribute', 'desoneration_reason', value)}
                                    options={desonerationReasonsFor(cst)}
                                />
                                {view.mark('attributes.desoneration_reason')}
                            </div>
                            <div className={view.cls('col-3', 'attributes.desoneration_rate')}>
                                <Input
                                    label={`Percentual (%)`}
                                    value={view.value('attributes.desoneration_rate')}
                                    {...view.focus('attributes.desoneration_rate')}
                                    change={value => onChange('attribute', 'desoneration_rate', decimal(value))}
                                />
                                <p className="input-hint">Vazio usa a alíquota do ICMS</p>
                                {view.mark('attributes.desoneration_rate')}
                            </div>
                        </div>
                    </div>
                }

                {tax === 'icms' &&
                    <div className="row">
                        {icmsHasFcp(cst) &&
                            <div className={view.cls('col-4', 'attributes.fcp_rate')}>
                                <Input
                                    label={`Alíquota FCP (%)`}
                                    value={view.value('attributes.fcp_rate')}
                                    {...view.focus('attributes.fcp_rate')}
                                    change={value => onChange('attribute', 'fcp_rate', decimal(value))}
                                />
                                <p className="input-hint">Fundo de combate à pobreza</p>
                                {view.mark('attributes.fcp_rate')}
                            </div>
                        }
                        <div className={view.cls('col-4', 'attributes.benefit_code')}>
                            <Input
                                label={`Código de benefício fiscal`}
                                value={view.value('attributes.benefit_code')}
                                {...view.focus('attributes.benefit_code')}
                                change={value => onChange('attribute', 'benefit_code', value)}
                            />
                            {view.mark('attributes.benefit_code')}
                        </div>
                    </div>
                }

                {tax === 'ipi' &&
                    <div className="row">
                        <div className={view.cls('col-4', 'attributes.framework_code')}>
                            <Input
                                label={`Código de enquadramento *`}
                                value={view.value('attributes.framework_code')}
                                {...view.focus('attributes.framework_code')}
                                change={value => onChange('attribute', 'framework_code', value)}
                            />
                            {view.mark('attributes.framework_code')}
                        </div>
                    </div>
                }

                {tax === 'ibscbs' && (() => {
                    // A tabela oficial tem 164 códigos, mas cada um pertence a
                    // UM CST: escolhido o CST, sobram poucas opções — para
                    // venda tributada integralmente, cinco. É o que transforma
                    // "pesquisar na tabela da SVRS" em "escolher na lista".
                    const options = classifications.filter(item => item.cst === cst);
                    const selected = classifications.find(
                        item => item.code === view.value('attributes.classification')
                    );

                    return (
                        <div className="row">
                            <div className={view.cls('col-6', 'attributes.classification')}>
                                <InputSelect
                                    menuInBody
                                    label={`Classificação tributária (cClassTrib) *`}
                                    options={options.map(item => ({
                                        label: `${item.code} — ${item.label}`,
                                        value: item.code,
                                    }))}
                                    value={view.value('attributes.classification')}
                                    change={value => onChange('attribute', 'classification', value)}
                                />
                                {view.mark('attributes.classification')}
                                {!cst &&
                                    <p className="input-hint">
                                        Escolha primeiro o CST de IBS/CBS para ver as classificações.
                                    </p>
                                }
                            </div>
                            <div className="col-6">
                                {/* Fora da calculadora, a nota sai sem os totais de
                                    IBS/CBS e alguém tem que preenchê-los à mão —
                                    avisar depois da emissão seria tarde. */}
                                {selected && !selected.calculator &&
                                    <p className="input-hint warning">
                                        Esta classificação sai <strong>sem os totais de IBS/CBS
                                        calculados automaticamente</strong> — o preenchimento fica
                                        manual. Confirme com a contabilidade se é mesmo este o
                                        enquadramento.
                                    </p>
                                }

                                {/* A opção mostra a descrição curta da tabela, que é
                                    resumo. O enquadramento se decide pelo texto legal,
                                    então ele aparece inteiro assim que o código é
                                    escolhido — com o artigo da LC 214 que o fundamenta. */}
                                {selected?.legal &&
                                    <p className="input-hint legal-text">{selected.legal}</p>
                                }
                            </div>
                        </div>
                    );
                })()}

                {tax === 'issqn' &&
                    <div className="row">
                        <div className={view.cls('col-3', 'attributes.service_code')}>
                            <Input
                                label={`Código do serviço *`}
                                value={view.value('attributes.service_code')}
                                {...view.focus('attributes.service_code')}
                                change={value => onChange('attribute', 'service_code', value)}
                            />
                            <p className="input-hint">Lista da LC 116</p>
                            {view.mark('attributes.service_code')}
                        </div>
                        <div className={view.cls('col-3', 'attributes.city_code')}>
                            <Input
                                label={`Código do município *`}
                                value={view.value('attributes.city_code')}
                                {...view.focus('attributes.city_code')}
                                change={value => onChange('attribute', 'city_code', onlyDigits(value))}
                            />
                            <p className="input-hint">IBGE do fato gerador</p>
                            {view.mark('attributes.city_code')}
                        </div>
                        <div className={view.cls('col-4', 'attributes.enforceability')}>
                            <InputSelect
                                label={`Exigibilidade *`}
                                value={view.value('attributes.enforceability')}
                                change={value => onChange('attribute', 'enforceability', value)}
                                options={ISSQN_ENFORCEABILITY_OPTIONS}
                            />
                            {view.mark('attributes.enforceability')}
                        </div>
                    </div>
                }

                {tax === 'difal' &&
                    <div className="row">
                        <div className={view.cls('col-4', 'attributes.interstate_rate')}>
                            <Input
                                label={`Alíquota interestadual (%) *`}
                                value={view.value('attributes.interstate_rate')}
                                {...view.focus('attributes.interstate_rate')}
                                change={value => onChange('attribute', 'interstate_rate', decimal(value))}
                            />
                            {view.mark('attributes.interstate_rate')}
                        </div>
                        <div className={view.cls('col-4', 'attributes.fcp_rate')}>
                            <Input
                                label={`FCP do destino (%)`}
                                value={view.value('attributes.fcp_rate')}
                                {...view.focus('attributes.fcp_rate')}
                                change={value => onChange('attribute', 'fcp_rate', decimal(value))}
                            />
                            {view.mark('attributes.fcp_rate')}
                        </div>
                    </div>
                }
            </>
        );
    };

/**
 * Campos de tributação de UM imposto (BDM-887).
 *
 * Serve a três lugares que precisam falar a mesma língua: a regra base da
 * natureza, a exceção da natureza e o ajuste de uma linha da nota. Os campos
 * que aparecem dependem do CST/CSOSN efetivo — mostrar ST onde ela não vai
 * para a NF-e só confunde.
 *
 * @param inherited regra que a natureza resolveu para este item; o campo
 *                  mostra o valor dela quando a linha não ajustou nada.
 * @param onChange (kind, key, value) — kind 'field' ou 'attribute'
 */
export const TaxRuleFields = ({ tax, rule, inherited = null, direction = 'out', onChange, classifications = [] }) => {
    const [editing, setEditing] = useState(null);

    return taxFields(tax, rule, inherited, onChange, direction, classifications, editing, setEditing);
};

export default TaxRuleFields;
